/**
 * Instruction Detail Page with Marketing Section
 * Allows viewing/editing property instruction details and marketing content
 */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Home, Building2, Camera, Layout, FileText,
  Plus, X, Loader2, Save, Upload, Trash2, CheckCircle,
  Circle, GripVertical, Image as ImageIcon
} from "lucide-react";
import { API } from "@/App";

const InstructionDetailPage = () => {
  const { instructionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [instruction, setInstruction] = useState(null);
  
  // Marketing form data
  const [marketingData, setMarketingData] = useState({
    summary: "",
    description: "",
    key_features: [],
    photos: [],
    floorplan_url: ""
  });
  const [newFeature, setNewFeature] = useState("");
  
  const photoInputRef = useRef(null);
  const floorplanInputRef = useRef(null);

  useEffect(() => {
    fetchInstruction();
  }, [instructionId]);

  const fetchInstruction = async () => {
    try {
      setLoading(true);
      // Try sales first, then lettings
      let res;
      try {
        res = await axios.get(`${API}/instructions/sales/${instructionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch {
        res = await axios.get(`${API}/instructions/lettings/${instructionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      const data = res.data;
      setInstruction(data);
      
      // Initialize marketing data
      setMarketingData({
        summary: data.summary || "",
        description: data.description || "",
        key_features: data.key_features || data.features || [],
        photos: data.photos || [],
        floorplan_url: data.floorplan_url || ""
      });
    } catch (error) {
      console.error("Error fetching instruction:", error);
      toast.error("Failed to load instruction");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMarketing = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/instructions/${instructionId}`,
        {
          summary: marketingData.summary,
          description: marketingData.description,
          key_features: marketingData.key_features,
          photos: marketingData.photos,
          floorplan_url: marketingData.floorplan_url
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Marketing details saved");
      fetchInstruction();
    } catch (error) {
      console.error("Error saving marketing:", error);
      toast.error("Failed to save marketing details");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const res = await axios.post(
          `${API}/instructions/${instructionId}/upload-media?media_type=photo`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            }
          }
        );
        
        if (res.data.photo) {
          setMarketingData(prev => ({
            ...prev,
            photos: [...prev.photos, res.data.photo]
          }));
        }
        toast.success(`Photo uploaded: ${file.name}`);
      } catch (error) {
        console.error("Error uploading photo:", error);
        toast.error(`Failed to upload: ${file.name}`);
      }
    }
    
    setUploading(false);
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const handleFloorplanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(
        `${API}/instructions/${instructionId}/upload-media?media_type=floorplan`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      setMarketingData(prev => ({
        ...prev,
        floorplan_url: res.data.url
      }));
      toast.success("Floorplan uploaded");
    } catch (error) {
      console.error("Error uploading floorplan:", error);
      toast.error("Failed to upload floorplan");
    } finally {
      setUploading(false);
      if (floorplanInputRef.current) floorplanInputRef.current.value = "";
    }
  };

  const handleDeletePhoto = async (index) => {
    try {
      await axios.delete(
        `${API}/instructions/${instructionId}/photos/${index}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMarketingData(prev => ({
        ...prev,
        photos: prev.photos.filter((_, i) => i !== index)
      }));
      toast.success("Photo deleted");
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error("Failed to delete photo");
    }
  };

  const handleAddFeature = () => {
    if (!newFeature.trim()) return;
    if (marketingData.key_features.length >= 10) {
      toast.error("Maximum 10 key features allowed");
      return;
    }
    setMarketingData(prev => ({
      ...prev,
      key_features: [...prev.key_features, newFeature.trim()]
    }));
    setNewFeature("");
  };

  const handleRemoveFeature = (index) => {
    setMarketingData(prev => ({
      ...prev,
      key_features: prev.key_features.filter((_, i) => i !== index)
    }));
  };

  const statusColors = {
    draft: "bg-slate-100 text-slate-700",
    pending_approval: "bg-amber-100 text-amber-700",
    approved: "bg-blue-100 text-blue-700",
    live: "bg-emerald-100 text-emerald-700",
    sold: "bg-purple-100 text-purple-700",
    withdrawn: "bg-red-100 text-red-700"
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!instruction) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Instruction not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold">{instruction.full_address}</h1>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Ref: {instruction.property_ref}</span>
                <span>•</span>
                <Badge variant="outline" className={instruction.type === "sales" ? "border-blue-300 text-blue-700" : "border-purple-300 text-purple-700"}>
                  {instruction.type === "sales" ? (
                    <><Home className="w-3 h-3 mr-1" /> Sales</>
                  ) : (
                    <><Building2 className="w-3 h-3 mr-1" /> Lettings</>
                  )}
                </Badge>
                <span>•</span>
                <Badge className={statusColors[instruction.status]}>{instruction.status?.replace("_", " ")}</Badge>
              </div>
            </div>
          </div>
          <Button onClick={handleSaveMarketing} disabled={saving} data-testid="save-marketing-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        {/* Workflow Progress */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Instruction Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <ProgressStep done={instruction.draft_approved} label="Draft Approved" />
              <ProgressStep done={instruction.contract_signed} label="Contract Signed" />
              <ProgressStep done={instruction.ids_uploaded} label="IDs Uploaded" />
              <ProgressStep done={instruction.marketing_approved} label="Marketing Approved" />
              <ProgressStep done={instruction.portal_live} label="Live on Portals" />
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="marketing" className="space-y-6">
          <TabsList>
            <TabsTrigger value="marketing" data-testid="tab-marketing">Marketing</TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos">Photos ({marketingData.photos.length})</TabsTrigger>
            <TabsTrigger value="floorplan" data-testid="tab-floorplan">Floorplan</TabsTrigger>
            <TabsTrigger value="details">Property Details</TabsTrigger>
          </TabsList>

          {/* Marketing Tab */}
          <TabsContent value="marketing">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Marketing Content
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary */}
                <div>
                  <Label htmlFor="summary">Summary / Headline (max 160 chars)</Label>
                  <Input
                    id="summary"
                    value={marketingData.summary}
                    onChange={(e) => setMarketingData(prev => ({ ...prev, summary: e.target.value }))}
                    placeholder="Brief headline for portal listings..."
                    maxLength={160}
                    data-testid="marketing-summary"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{marketingData.summary.length}/160 characters</p>
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description">Full Description</Label>
                  <Textarea
                    id="description"
                    value={marketingData.description}
                    onChange={(e) => setMarketingData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Full property description for marketing..."
                    rows={8}
                    data-testid="marketing-description"
                  />
                </div>

                {/* Key Features */}
                <div>
                  <Label>Key Features (max 10)</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="Add a key feature..."
                      onKeyPress={(e) => e.key === "Enter" && handleAddFeature()}
                      data-testid="new-feature-input"
                    />
                    <Button type="button" onClick={handleAddFeature} disabled={marketingData.key_features.length >= 10}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {marketingData.key_features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-md">
                        <span className="text-sm font-medium text-slate-600">{index + 1}.</span>
                        <span className="flex-1 text-sm">{feature}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleRemoveFeature(index)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </li>
                    ))}
                    {marketingData.key_features.length === 0 && (
                      <p className="text-sm text-muted-foreground">No key features added yet</p>
                    )}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="w-5 h-5" />
                    Property Photos
                  </CardTitle>
                  <div>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <Button onClick={() => photoInputRef.current?.click()} disabled={uploading} data-testid="upload-photos-btn">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Photos
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {marketingData.photos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {marketingData.photos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <div className="aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden">
                          <img
                            src={`${API}${photo.url}`}
                            alt={`Property photo ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                          />
                        </div>
                        <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                          #{photo.order || index + 1}
                        </div>
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeletePhoto(index)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                    <ImageIcon className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-muted-foreground">No photos uploaded yet</p>
                    <Button variant="outline" className="mt-3" onClick={() => photoInputRef.current?.click()}>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Photos
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Floorplan Tab */}
          <TabsContent value="floorplan">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Layout className="w-5 h-5" />
                    Floorplan
                  </CardTitle>
                  <div>
                    <input
                      ref={floorplanInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFloorplanUpload}
                      className="hidden"
                    />
                    <Button onClick={() => floorplanInputRef.current?.click()} disabled={uploading} data-testid="upload-floorplan-btn">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {marketingData.floorplan_url ? "Replace Floorplan" : "Upload Floorplan"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {marketingData.floorplan_url ? (
                  <div className="max-w-2xl mx-auto">
                    <div className="aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden">
                      <img
                        src={`${API}${marketingData.floorplan_url}`}
                        alt="Floorplan"
                        className="w-full h-full object-contain"
                        onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                    <Layout className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-muted-foreground">No floorplan uploaded yet</p>
                    <Button variant="outline" className="mt-3" onClick={() => floorplanInputRef.current?.click()}>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Floorplan
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Property Details Tab */}
          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle>Property Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <DetailItem label="Property Type" value={instruction.property_type?.replace("_", " ")} />
                  <DetailItem label="Bedrooms" value={instruction.bedrooms} />
                  <DetailItem label="Bathrooms" value={instruction.bathrooms} />
                  <DetailItem label="Receptions" value={instruction.receptions} />
                  <DetailItem label="Tenure" value={instruction.tenure} />
                  <DetailItem label="EPC Rating" value={instruction.epc_rating} />
                  <DetailItem label="Council Tax" value={instruction.council_tax_band} />
                  {instruction.type === "sales" && (
                    <>
                      <DetailItem label="Asking Price" value={`£${instruction.asking_price?.toLocaleString()}`} />
                      <DetailItem label="Agency Type" value={instruction.agency_type} />
                      <DetailItem label="Fee" value={`${instruction.fee_percentage}%`} />
                    </>
                  )}
                  {instruction.type === "lettings" && (
                    <>
                      <DetailItem label="Monthly Rent" value={`£${instruction.monthly_rent?.toLocaleString()} pcm`} />
                      <DetailItem label="Management" value={instruction.management_type?.replace("_", " ")} />
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const ProgressStep = ({ done, label }) => (
  <div className="flex items-center gap-2">
    {done ? (
      <CheckCircle className="w-5 h-5 text-emerald-500" />
    ) : (
      <Circle className="w-5 h-5 text-slate-300" />
    )}
    <span className={done ? "text-slate-700 font-medium" : "text-slate-400"}>{label}</span>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div>
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="font-medium capitalize">{value || "-"}</p>
  </div>
);

export default InstructionDetailPage;
